import { useMemo } from 'react';
import { useChannelStateContext } from 'stream-chat-react';

import { useChatClient } from '@bizzabo/chat-core';

import useVideoEnable from '../../../../hooks/useVideoEnable';
import { isMissedCall } from '../../PrivateChat/components/MissedCallMessageWrapper/types';
import { ExtendedGetStreamMessage } from '../../PrivateChat/components/UIMessage/types';
import { useMessages } from '../../contexts/MessagesContext';

export enum ChannelStatus {
    Welcome,
    HostSend,
    GuestSend,
    StartMeeting,
    CanMeet,
}

const useChannelStatus = () => {
    const { eventUniqueName } = useMessages();
    const { data: shouldDisplay } = useVideoEnable(eventUniqueName);
    const { client } = useChatClient();
    const { messages } = useChannelStateContext();

    const checkStatusOfChannel = () => {
        let channelStatus = ChannelStatus.Welcome;
        let startMeetingIndex = 0;
        if (!messages.length) {
            return {
                channelStatus,
                startMeetingIndex,
            };
        }

        let canMeet = false;
        let firstSenderCount = 0;
        let secondSenderCount = 0;
        const {
            user: { id: firstSenderId },
        } = messages[0];

        for (const [index, message] of messages.entries()) {
            const messageType = (message as ExtendedGetStreamMessage).metadata?.type;
            if (isMissedCall(messageType)) {
                canMeet = true;
                break;
            }
            if (secondSenderCount === 1) {
                canMeet = true;
            }
            if (firstSenderId === message.user.id) {
                firstSenderCount++;
            } else {
                startMeetingIndex = index;
                secondSenderCount++;
            }
            if (firstSenderCount > 1 && secondSenderCount > 1) {
                break;
            }
        }

        if (canMeet) {
            return {
                channelStatus: ChannelStatus.CanMeet,
                startMeetingIndex,
            };
        }

        if (!secondSenderCount) {
            //only one side of the chat send messages
            channelStatus =
                firstSenderId === client.userID ? ChannelStatus.HostSend : ChannelStatus.GuestSend;
            return {
                channelStatus,
                startMeetingIndex,
            };
        }

        if (secondSenderCount === 1 && !canMeet) {
            //one replay
            return {
                channelStatus: ChannelStatus.StartMeeting,
                startMeetingIndex,
            };
        }
    };

    const { channelStatus, startMeetingIndex } = useMemo(() => {
        if (!shouldDisplay) {
            return {
                channelStatus: ChannelStatus.Welcome,
                startMeetingIndex: 0,
            };
        }
        return checkStatusOfChannel();
    }, [messages.length]);

    return { channelStatus, startMeetingIndex };
};

export default useChannelStatus;
